// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — NOAA library rows that a Pachamama Studios stream replaces (owner,
 * 2026-10-30, decision C9 of the 2026-10-11 SOS catalogue review: free
 * accounts keep the library rows, paid ones see the stream twin instead).
 *
 * A library row's `libraryTwin` names its stream by the index's stable
 * `name` (`content/library/stream-twins.json`, written into the index by
 * `scripts/build-noaa-library.ts`). The row is hidden where the stream is
 * in the catalogue and the plan plays it; anywhere else (no plan for it,
 * the stream missing from the index, the account not known yet) the row
 * stays. AYNI 3D, 2D and the Museum Edition read the same field.
 */

import type { Dataset } from '../types'
import { outsidePlan } from './datasetTaxonomy'

/**
 * The ids of the library rows to hide, given the plan's packages (null
 * while the account is unknown: nothing is hidden, so no row flickers out
 * before the plan is known).
 */
export function twinHiddenIds(
  datasets: readonly Dataset[],
  packages: ReadonlySet<string> | 'all' | null,
  educationStreams = false,
): ReadonlySet<string> {
  const hidden = new Set<string>()
  if (!packages) return hidden
  const streams = new Map<string, Dataset>()
  for (const d of datasets) if (d.streamName) streams.set(d.streamName, d)
  for (const d of datasets) {
    const stream = d.libraryTwin ? streams.get(d.libraryTwin) : undefined
    if (stream && outsidePlan(stream, packages, educationStreams) === null) hidden.add(d.id)
  }
  return hidden
}
